import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Rabbit,
  Cat,
  Bird,
  Squirrel,
  Turtle,
  Feather,
  TreePine,
  Waves,
  Castle,
  Rocket,
  BookOpen,
  Compass,
  Moon,
  CreditCard,
  Volume2,
  Palette,
  Lock,
  Heart,
  Zap,
  Wand2,
  Smile,
  ChevronDown,
  ChevronUp,
  Clock,
  Flame,
  Gem,
  CircleDot,
  Fish,
  Snowflake,
  Mouse,
  Flower2,
  Mountain,
  Home,
  Sun,
  Search,
  Users,
} from 'lucide-preact';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { MAX_NAME_LENGTH } from '../lib/constants';
import { createCheckoutSession } from '../lib/api';
import type { StoryType, Animal, Setting, StoryMood, StoryLength, StoryChoices } from '../lib/types';

type Step = 'hero' | 'name' | 'storyType' | 'setting' | 'confirm' | 'redirecting';

const LOADING_MESSAGES = [
  'Connecting to secure payment...',
  'Preparing your story order...',
];

// Color styles using CSS variables from our theme
const THEME_COLORS = {
  rust: { bg: 'rgba(176, 107, 79, 0.15)', bgSelected: 'rgba(176, 107, 79, 0.25)', text: 'rgb(176, 107, 79)' },
  berry: { bg: 'rgba(156, 89, 112, 0.15)', bgSelected: 'rgba(156, 89, 112, 0.25)', text: 'rgb(156, 89, 112)' },
  gold: { bg: 'rgba(212, 165, 92, 0.18)', bgSelected: 'rgba(212, 165, 92, 0.32)', text: 'rgb(182, 135, 62)' },
  moss: { bg: 'rgba(122, 142, 102, 0.15)', bgSelected: 'rgba(122, 142, 102, 0.25)', text: 'rgb(122, 142, 102)' },
  night: { bg: 'rgba(72, 68, 85, 0.12)', bgSelected: 'rgba(72, 68, 85, 0.22)', text: 'rgb(72, 68, 85)' },
} as const;

type ThemeColor = keyof typeof THEME_COLORS;

// Hero options with rich descriptions
const HEROES = [
  {
    value: 'rabbit' as Animal,
    label: 'Brave Bunny',
    icon: Rabbit,
    trait: 'Quick & Clever',
    color: 'berry' as ThemeColor,
  },
  {
    value: 'fox' as Animal,
    label: 'Curious Fox',
    icon: Cat,
    trait: 'Smart & Bold',
    color: 'rust' as ThemeColor,
  },
  {
    value: 'owl' as Animal,
    label: 'Wise Owl',
    icon: Bird,
    trait: 'Thoughtful',
    color: 'night' as ThemeColor,
  },
  {
    value: 'bear' as Animal,
    label: 'Gentle Bear',
    icon: Squirrel,
    trait: 'Strong & Kind',
    color: 'gold' as ThemeColor,
  },
  {
    value: 'turtle' as Animal,
    label: 'Steady Turtle',
    icon: Turtle,
    trait: 'Patient',
    color: 'moss' as ThemeColor,
  },
  {
    value: 'deer' as Animal,
    label: 'Swift Deer',
    icon: Feather,
    trait: 'Graceful',
    color: 'rust' as ThemeColor,
  },
  {
    value: 'dragon' as Animal,
    label: 'Friendly Dragon',
    icon: Flame,
    trait: 'Magical & Warm',
    color: 'rust' as ThemeColor,
  },
  {
    value: 'unicorn' as Animal,
    label: 'Gentle Unicorn',
    icon: Gem,
    trait: 'Pure & Kind',
    color: 'berry' as ThemeColor,
  },
  {
    value: 'hedgehog' as Animal,
    label: 'Cozy Hedgehog',
    icon: CircleDot,
    trait: 'Shy & Sweet',
    color: 'gold' as ThemeColor,
  },
  {
    value: 'otter' as Animal,
    label: 'Playful Otter',
    icon: Fish,
    trait: 'Fun & Joyful',
    color: 'moss' as ThemeColor,
  },
  {
    value: 'penguin' as Animal,
    label: 'Plucky Penguin',
    icon: Snowflake,
    trait: 'Brave & Loyal',
    color: 'night' as ThemeColor,
  },
  {
    value: 'mouse' as Animal,
    label: 'Little Mouse',
    icon: Mouse,
    trait: 'Tiny & Mighty',
    color: 'gold' as ThemeColor,
  },
];

const STORY_TYPES = [
  {
    value: 'fable' as StoryType,
    label: 'Fable',
    icon: BookOpen,
    description: 'A tale with a gentle lesson',
    color: 'gold' as ThemeColor,
  },
  {
    value: 'adventure' as StoryType,
    label: 'Adventure',
    icon: Compass,
    description: 'An exciting journey',
    color: 'moss' as ThemeColor,
  },
  {
    value: 'bedtime' as StoryType,
    label: 'Bedtime',
    icon: Moon,
    description: 'A calm, peaceful story',
    color: 'night' as ThemeColor,
  },
  {
    value: 'mystery' as StoryType,
    label: 'Mystery',
    icon: Search,
    description: 'A puzzle to solve',
    color: 'berry' as ThemeColor,
  },
  {
    value: 'friendship' as StoryType,
    label: 'Friendship',
    icon: Users,
    description: 'A heartwarming bond',
    color: 'rust' as ThemeColor,
  },
];

const SETTINGS = [
  {
    value: 'forest' as Setting,
    label: 'Enchanted Forest',
    icon: TreePine,
    atmosphere: 'Ancient trees & sparkling streams',
    color: 'moss' as ThemeColor,
  },
  {
    value: 'ocean' as Setting,
    label: 'Deep Ocean',
    icon: Waves,
    atmosphere: 'Coral kingdoms & hidden treasures',
    color: 'night' as ThemeColor,
  },
  {
    value: 'castle' as Setting,
    label: 'Cloud Castle',
    icon: Castle,
    atmosphere: 'Crystal towers & secret passages',
    color: 'berry' as ThemeColor,
  },
  {
    value: 'space' as Setting,
    label: 'Starry Cosmos',
    icon: Rocket,
    atmosphere: 'Twinkling stars & friendly planets',
    color: 'night' as ThemeColor,
  },
  {
    value: 'meadow' as Setting,
    label: 'Sunny Meadow',
    icon: Flower2,
    atmosphere: 'Wildflowers & gentle breezes',
    color: 'gold' as ThemeColor,
  },
  {
    value: 'mountain' as Setting,
    label: 'Misty Mountain',
    icon: Mountain,
    atmosphere: 'High peaks & hidden caves',
    color: 'night' as ThemeColor,
  },
  {
    value: 'village' as Setting,
    label: 'Cozy Village',
    icon: Home,
    atmosphere: 'Friendly neighbors & warm hearths',
    color: 'rust' as ThemeColor,
  },
  {
    value: 'garden' as Setting,
    label: 'Secret Garden',
    icon: Flower2,
    atmosphere: 'Magical blooms & tiny friends',
    color: 'berry' as ThemeColor,
  },
  {
    value: 'arctic' as Setting,
    label: 'Frozen North',
    icon: Snowflake,
    atmosphere: 'Shimmering ice & aurora lights',
    color: 'night' as ThemeColor,
  },
  {
    value: 'desert' as Setting,
    label: 'Golden Desert',
    icon: Sun,
    atmosphere: 'Sand dunes & oasis dreams',
    color: 'gold' as ThemeColor,
  },
];

const MOODS = [
  {
    value: 'cozy' as StoryMood,
    label: 'Cozy',
    icon: Heart,
    description: 'Warm and comforting',
    color: 'bg-rose-50 text-rose-600',
  },
  {
    value: 'exciting' as StoryMood,
    label: 'Exciting',
    icon: Zap,
    description: 'Fast-paced thrills',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    value: 'magical' as StoryMood,
    label: 'Magical',
    icon: Wand2,
    description: 'Wonder and enchantment',
    color: 'bg-violet-50 text-violet-600',
  },
  {
    value: 'funny' as StoryMood,
    label: 'Funny',
    icon: Smile,
    description: 'Silly and playful',
    color: 'bg-emerald-50 text-emerald-600',
  },
];

const LENGTHS = [
  {
    value: 'short' as StoryLength,
    label: '2 min',
    words: '~200 words',
    description: 'Quick bedtime tale',
  },
  {
    value: 'medium' as StoryLength,
    label: '4 min',
    words: '~350 words',
    description: 'Perfect length',
  },
  {
    value: 'long' as StoryLength,
    label: '6 min',
    words: '~500 words',
    description: 'Extended adventure',
  },
];

export function Create(_props: RoutableProps) {
  const [step, setStep] = useState<Step>('hero');
  const [hero, setHero] = useState<Animal | null>(null);
  const [heroName, setHeroName] = useState('');
  const [storyType, setStoryType] = useState<StoryType | null>(null);
  const [setting, setSetting] = useState<Setting | null>(null);
  const [mood, setMood] = useState<StoryMood | null>(null);
  const [length, setLength] = useState<StoryLength>('medium');
  const [showExtras, setShowExtras] = useState(false);
  const [error, setError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);

  const selectedHero = HEROES.find((h) => h.value === hero);
  const selectedStoryType = STORY_TYPES.find((t) => t.value === storyType);
  const selectedSetting = SETTINGS.find((s) => s.value === setting);
  const steps: Step[] = ['hero', 'name', 'storyType', 'setting', 'confirm'];
  const currentStepIndex = steps.indexOf(step);

  const handleNameSubmit = (e: Event) => {
    e.preventDefault();
    const trimmedName = heroName.trim();
    if (!trimmedName) {
      setError('Please give your hero a name');
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      setError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
      return;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
      setError('Please use only letters');
      return;
    }
    setError('');
    setStep('storyType');
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex - 1]);
    } else {
      route('/');
    }
  };

  const handleCheckout = async () => {
    if (!hero || !storyType || !setting) return;

    setStep('redirecting');
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 2000);

    try {
      const choices: StoryChoices = {
        childName: heroName.trim(),
        storyType,
        animal: hero,
        setting,
        mood: mood || undefined,
        length,
      };

      const { url } = await createCheckoutSession(choices);
      clearInterval(messageInterval);

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      clearInterval(messageInterval);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStep('confirm');
    }
  };

  if (step === 'redirecting') {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <div className="text-center animate-in">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 w-32 h-32 rounded-full bg-gold/20 blur-xl pulse-ring" />
            <div className="relative w-32 h-32 rounded-full bg-parchment border-ornate flex items-center justify-center">
              <CreditCard className="w-14 h-14 text-rust float" strokeWidth={1.25} />
            </div>
          </div>
          <LoadingSpinner message={loadingMessage} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-warm">
      <div className="container-custom py-10 sm:py-16">
        <div className="max-w-2xl mx-auto">
          {/* Progress indicator */}
          <div className="mb-12 animate-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-center gap-3">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      i < currentStepIndex
                        ? 'bg-rust'
                        : i === currentStepIndex
                          ? 'bg-gold ring-4 ring-gold/20'
                          : 'bg-ink/10'
                    }`}
                  />
                  {i < steps.length - 1 && (
                    <div
                      className={`w-12 h-0.5 transition-all duration-300 ${
                        i < currentStepIndex ? 'bg-rust' : 'bg-ink/10'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {step === 'hero' && (
            <div className="animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-display font-medium text-ink mb-4">
                  Choose your hero
                </h1>
                <p className="text-lg text-ink/60">Who will star in your story?</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
                {HEROES.map((h) => {
                  const colors = THEME_COLORS[h.color];
                  const isSelected = hero === h.value;
                  return (
                    <button
                      key={h.value}
                      onClick={() => setHero(h.value)}
                      className={`group relative p-6 rounded-2xl transition-all duration-300 cursor-pointer
                        ${
                          isSelected
                            ? 'bg-parchment border-ornate shadow-story selection-pop'
                            : 'bg-cream/50 border border-gold/10 hover:border-gold/30 hover:shadow-story'
                        }
                      `}
                    >
                      {isSelected && (
                        <div
                          className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full"
                          style={{ background: colors.bgSelected }}
                        />
                      )}
                      <div
                        className="w-14 h-14 rounded-2xl mb-4 flex items-center justify-center transition-all"
                        style={{ background: isSelected ? colors.bgSelected : colors.bg }}
                      >
                        <h.icon
                          size={28}
                          strokeWidth={1.5}
                          className="icon-bounce transition-colors"
                          style={{ color: isSelected ? colors.text : undefined }}
                        />
                      </div>
                      <div className="font-display font-medium text-ink text-lg mb-1">{h.label}</div>
                      <div className="text-sm text-ink/40">{h.trait}</div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-ink/50 hover:text-ink transition-colors cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
                <button
                  onClick={() => hero && setStep('name')}
                  disabled={!hero}
                  className={`flex-1 px-6 py-4 rounded-2xl font-medium text-lg flex items-center justify-center gap-2
                    transition-all duration-300 cursor-pointer
                    ${
                      hero
                        ? 'bg-rust text-cream shadow-story hover:shadow-story-hover hover:-translate-y-0.5'
                        : 'bg-ink/10 text-ink/30 cursor-not-allowed'
                    }
                  `}
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 'name' && selectedHero && (
            <div className="animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-parchment border-ornate mb-6">
                  <selectedHero.icon size={36} className="text-rust" strokeWidth={1.5} />
                </div>
                <h1 className="text-4xl sm:text-5xl font-display font-medium text-ink mb-4">
                  Name your hero
                </h1>
                <p className="text-lg text-ink/60">
                  What shall we call this {selectedHero.label.toLowerCase()}?
                </p>
              </div>

              <form onSubmit={handleNameSubmit} className="max-w-sm mx-auto">
                <div className="relative mb-6">
                  <input
                    type="text"
                    value={heroName}
                    onInput={(e) => setHeroName((e.target as HTMLInputElement).value)}
                    placeholder="Enter a name..."
                    maxLength={MAX_NAME_LENGTH}
                    className="w-full px-6 py-5 text-2xl text-center font-display text-ink
                             bg-parchment border-ornate rounded-2xl
                             focus:outline-none focus:ring-4 focus:ring-gold/20
                             placeholder:text-ink/20 transition-all"
                    autoFocus
                  />
                  {heroName && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-ink/30">
                      {heroName.length}/{MAX_NAME_LENGTH}
                    </span>
                  )}
                </div>

                {error && (
                  <p className="mb-6 text-sm text-rust text-center bg-rust/10 py-3 px-4 rounded-xl">
                    {error}
                  </p>
                )}

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-3 text-ink/50 hover:text-ink transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                             flex items-center justify-center gap-2 shadow-story
                             hover:shadow-story-hover hover:-translate-y-0.5
                             transition-all duration-300 cursor-pointer"
                  >
                    Continue
                    <ArrowRight size={18} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {step === 'storyType' && (
            <div className="animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-display font-medium text-ink mb-4">
                  What kind of story?
                </h1>
                <p className="text-lg text-ink/60">Choose the tale for {heroName}</p>
              </div>

              <div className="grid gap-4 mb-10">
                {STORY_TYPES.map((type) => {
                  const colors = THEME_COLORS[type.color];
                  const isSelected = storyType === type.value;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setStoryType(type.value)}
                      className={`group flex items-center gap-6 p-6 rounded-2xl transition-all duration-300 cursor-pointer text-left
                        ${
                          isSelected
                            ? 'bg-parchment border-ornate shadow-story selection-pop'
                            : 'bg-cream/50 border border-gold/10 hover:border-gold/30 hover:shadow-story'
                        }
                      `}
                    >
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all"
                        style={{ background: isSelected ? colors.bgSelected : colors.bg }}
                      >
                        <type.icon
                          size={28}
                          strokeWidth={1.5}
                          className="icon-bounce transition-colors"
                          style={{ color: isSelected ? colors.text : undefined }}
                        />
                      </div>
                      <div>
                        <div className="font-display font-medium text-ink text-xl mb-1">{type.label}</div>
                        <div className="text-ink/50">{type.description}</div>
                      </div>
                      {isSelected && (
                        <div
                          className="ml-auto w-3 h-3 rounded-full flex-shrink-0"
                          style={{ background: colors.bgSelected }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-ink/50 hover:text-ink transition-colors cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
                <button
                  onClick={() => storyType && setStep('setting')}
                  disabled={!storyType}
                  className={`flex-1 px-6 py-4 rounded-2xl font-medium text-lg flex items-center justify-center gap-2
                    transition-all duration-300 cursor-pointer
                    ${
                      storyType
                        ? 'bg-rust text-cream shadow-story hover:shadow-story-hover hover:-translate-y-0.5'
                        : 'bg-ink/10 text-ink/30 cursor-not-allowed'
                    }
                  `}
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 'setting' && (
            <div className="animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-display font-medium text-ink mb-4">
                  Pick the world
                </h1>
                <p className="text-lg text-ink/60">Where does {heroName}'s adventure unfold?</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {SETTINGS.map((s) => {
                  const colors = THEME_COLORS[s.color];
                  const isSelected = setting === s.value;
                  return (
                    <button
                      key={s.value}
                      onClick={() => setSetting(s.value)}
                      className={`group relative p-6 rounded-2xl transition-all duration-300 cursor-pointer text-left
                        ${
                          isSelected
                            ? 'bg-parchment border-ornate shadow-story selection-pop'
                            : 'bg-cream/50 border border-gold/10 hover:border-gold/30 hover:shadow-story'
                        }
                      `}
                    >
                      {isSelected && (
                        <div
                          className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full"
                          style={{ background: colors.bgSelected }}
                        />
                      )}
                      <div
                        className="w-14 h-14 rounded-2xl mb-4 flex items-center justify-center transition-all"
                        style={{ background: isSelected ? colors.bgSelected : colors.bg }}
                      >
                        <s.icon
                          size={28}
                          strokeWidth={1.5}
                          className="icon-bounce transition-colors"
                          style={{ color: isSelected ? colors.text : undefined }}
                        />
                      </div>
                      <div className="font-display font-medium text-ink text-lg mb-1">{s.label}</div>
                      <div className="text-sm text-ink/40 leading-snug">{s.atmosphere}</div>
                    </button>
                  );
                })}
              </div>

              {/* Collapsible extras panel */}
              <div className="mb-8">
                <button
                  onClick={() => setShowExtras(!showExtras)}
                  className="w-full flex items-center justify-center gap-2 py-3 text-ink/50 hover:text-ink/70
                           transition-colors cursor-pointer"
                >
                  <span className="text-sm">Customize mood & length</span>
                  {showExtras ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showExtras && (
                  <div className="mt-4 space-y-6 animate-in" style={{ animationDelay: '0s' }}>
                    {/* Mood selection */}
                    <div>
                      <label className="block text-sm text-ink/60 mb-3 text-center">Story mood</label>
                      <div className="flex flex-wrap justify-center gap-2">
                        {MOODS.map((m) => (
                          <button
                            key={m.value}
                            onClick={() => setMood(mood === m.value ? null : m.value)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                                     transition-all cursor-pointer
                              ${
                                mood === m.value
                                  ? `${m.color} ring-2 ring-offset-2 ring-current`
                                  : 'bg-cream/80 text-ink/60 hover:bg-cream border border-gold/10'
                              }
                            `}
                          >
                            <m.icon size={16} strokeWidth={1.5} />
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Length selection */}
                    <div>
                      <label className="block text-sm text-ink/60 mb-3 text-center">
                        <Clock size={14} className="inline mr-1.5" />
                        Story length
                      </label>
                      <div className="flex justify-center gap-2">
                        {LENGTHS.map((l) => (
                          <button
                            key={l.value}
                            onClick={() => setLength(l.value)}
                            className={`flex flex-col items-center px-5 py-3 rounded-xl transition-all cursor-pointer
                              ${
                                length === l.value
                                  ? 'bg-parchment border-ornate shadow-story'
                                  : 'bg-cream/50 border border-gold/10 hover:border-gold/30'
                              }
                            `}
                          >
                            <span className="font-medium text-ink">{l.label}</span>
                            <span className="text-xs text-ink/40">{l.words}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <p className="mb-6 text-sm text-rust text-center bg-rust/10 py-3 px-4 rounded-xl">
                  {error}
                </p>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-ink/50 hover:text-ink transition-colors cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
                <button
                  onClick={() => setting && setStep('confirm')}
                  disabled={!setting}
                  className={`flex-1 px-6 py-4 rounded-2xl font-medium text-lg flex items-center justify-center gap-2
                    transition-all duration-300 cursor-pointer
                    ${
                      setting
                        ? 'bg-rust text-cream shadow-story hover:shadow-story-hover hover:-translate-y-0.5'
                        : 'bg-ink/10 text-ink/30 cursor-not-allowed'
                    }
                  `}
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 'confirm' && selectedHero && selectedStoryType && selectedSetting && (
            <div className="animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-display font-medium text-ink mb-4">
                  Review Your Story
                </h1>
                <p className="text-lg text-ink/60">Almost ready to create {heroName}'s adventure</p>
              </div>

              {/* Order Summary */}
              <div className="bg-cream/80 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border-ornate mb-8">
                <h2 className="font-display text-xl font-medium text-ink mb-6">Your Story Details</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <selectedHero.icon size={24} className="text-rust" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-sm text-ink/50">Hero</div>
                      <div className="font-medium text-ink">{heroName} the {selectedHero.label}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <selectedStoryType.icon size={24} className="text-rust" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-sm text-ink/50">Story Type</div>
                      <div className="font-medium text-ink">{selectedStoryType.label} - {selectedStoryType.description}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <selectedSetting.icon size={24} className="text-rust" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-sm text-ink/50">Setting</div>
                      <div className="font-medium text-ink">{selectedSetting.label}</div>
                    </div>
                  </div>

                  {/* Optional extras shown inline */}
                  {(mood || length !== 'medium') && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {mood && (() => {
                        const selectedMood = MOODS.find(m => m.value === mood);
                        return selectedMood ? (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${selectedMood.color}`}>
                            <selectedMood.icon size={14} />
                            {selectedMood.label}
                          </span>
                        ) : null;
                      })()}
                      {length !== 'medium' && (() => {
                        const selectedLength = LENGTHS.find(l => l.value === length);
                        return selectedLength ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-ink/5 text-ink/70">
                            <Clock size={14} />
                            {selectedLength.label} read
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                <div className="border-t border-gold/20 pt-6">
                  <h3 className="font-medium text-ink mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    {[
                      { icon: Sparkles, text: 'Unlimited personalized stories' },
                      { icon: Volume2, text: 'Professional voice narration' },
                      { icon: Palette, text: 'Custom illustrations for each story' },
                      { icon: BookOpen, text: 'Save your entire story library' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <item.icon size={18} className="text-moss flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-ink/70">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Subscription Price */}
              <div className="bg-parchment rounded-2xl p-6 border-ornate text-center mb-8">
                <div className="inline-flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-display font-medium text-rust">$20</span>
                  <span className="text-lg text-ink/50">/month</span>
                </div>
                <p className="text-ink/50 text-sm">Cancel anytime</p>
              </div>

              {error && (
                <p className="mb-6 text-sm text-rust text-center bg-rust/10 py-3 px-4 rounded-xl">
                  {error}
                </p>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-ink/50 hover:text-ink transition-colors cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
                <button
                  onClick={handleCheckout}
                  className="flex-1 px-6 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                           flex items-center justify-center gap-3 shadow-story
                           hover:shadow-story-hover hover:-translate-y-0.5
                           transition-all duration-300 cursor-pointer"
                >
                  <CreditCard size={20} strokeWidth={1.5} />
                  Subscribe & Create
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 mt-6 text-ink/40 text-sm">
                <Lock size={14} />
                <span>Secure payment powered by Stripe</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
